import { useState, useRef, useEffect } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight, Lock } from "lucide-react";
import { trackAndSend } from "../lib/metaEvents";
import { useOnce } from "../lib/useOnce";

export default function Checkout() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const emailRef = useRef(null);

  useOnce(() => {
    trackAndSend("InitiateCheckout", {
      content_name: "AuthorityOS Diagnostic",
      content_type: "product",
      content_ids: ["authority_os_diagnostic_97"],
      value: 97.0,
      currency: "USD",
      num_items: 1,
    });
  });

  // Auto-focus the email input on mount — one less tap for the buyer.
  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Email required");
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        email,
        origin_url: window.location.origin,
      });
      localStorage.setItem(
        "ao_pending_purchase",
        JSON.stringify({ purchase_id: data.purchase_id, email })
      );
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-120px)] flex items-center justify-center px-6 py-16" data-testid="checkout-page">
      <div className="w-full max-w-md">
        <div className="eyebrow text-center">Purchase · Step 01 of 03</div>
        <h1 className="mt-6 font-serif text-4xl md:text-5xl text-[#1A1915] leading-[1.05] text-center">
          Ten years of guessing.<br />
          <span className="italic">Or fifteen minutes of truth.</span>
        </h1>
        <p className="mt-5 text-sm text-[#78716C] text-center max-w-sm mx-auto leading-relaxed">
          One email. No account. No calls. Your bottleneck named, your five-step install plan drawn — before you finish your next coffee.
        </p>

        <div className="mt-6 flex items-center justify-center gap-3">
          <span className="text-lg text-[#78716C] line-through">$149</span>
          <span className="font-serif text-3xl text-[#1A1915]">$97</span>
        </div>

        <form
          onSubmit={handleSubmit}
          className="mt-10 border border-[#1A1915] p-6 bg-[#FAFAF9]"
          data-testid="checkout-form"
        >
          <label htmlFor="ao-email" className="eyebrow block">
            Your email
          </label>
          <input
            ref={emailRef}
            id="ao-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            className="mt-3 w-full border-b border-[#1A1915] pb-2 bg-transparent text-lg text-[#1A1915] focus:outline-none focus:border-[#B8863C]"
            data-testid="checkout-email-input"
          />
          <button
            type="submit"
            disabled={loading}
            className="btn-primary mt-6 w-full"
            data-testid="checkout-submit-button"
          >
            {loading ? (
              "Redirecting to Stripe…"
            ) : (
              <>
                Continue to payment · $97
                <ArrowRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
              </>
            )}
          </button>
        </form>

        <div className="mt-6 flex items-center justify-center gap-2 text-xs text-[#78716C]">
          <Lock className="w-3 h-3" strokeWidth={1.5} />
          Secured by Stripe · No account required
        </div>
        <div className="mt-2 text-[11px] text-[#78716C] text-center tracking-wide">
          Card · Apple Pay · Google Pay · Klarna · Link
        </div>

        <p className="mt-6 text-[11px] text-[#78716C] text-center leading-relaxed">
          Digital product · Consent to{" "}
          <a href="/refund" className="underline hover:text-[#1A1915]">
            no-refund policy
          </a>{" "}
          required at checkout.
        </p>
      </div>
    </div>
  );
}
